import type { ReactNode } from "react";
import { useTranslations, useLocale as useIntlLocale } from "next-intl";

import { L } from "@/extensions/lida/lida";
import { hasCatalog } from "./locales";

/**
 * `<T>` — the one way to put translated copy on screen.
 *
 * Replaces WorldLida's `<L text="…">`. The difference that matters: `<T>`
 * resolves from the message catalog **synchronously during render**, so the
 * HTML Google and Bing receive is already in the right language. `<L>` could
 * only ever translate after hydration, which left every non-English page
 * indexed in English.
 *
 * Works in both Server and Client Components — `useTranslations` is a real
 * hook only in the latter.
 *
 * Two escape hatches remain, and both are deliberate:
 *
 *  1. A locale with no catalog yet (see `CATALOG_LOCALES`) renders the English
 *     message wrapped in `<L>`, i.e. exactly the old behaviour. Nothing
 *     regresses while the remaining catalogs get written.
 *  2. Copy that no catalog can hold — product titles, supplier descriptions,
 *     user-generated text — keeps using `<L>` directly. That is what WorldLida
 *     is for now.
 */

/**
 * Inline markup allowed inside a message, e.g.
 * `"The <span>largest beauty marketplace</span> for women."`
 *
 * Kept to presentational tags on purpose: anything that needs an href or a
 * handler should be composed in JSX around several messages instead, so
 * translators never have to edit markup that can break the page.
 */
const RICH_TAGS = {
  span: (chunks: ReactNode) => <span>{chunks}</span>,
  em: (chunks: ReactNode) => <em>{chunks}</em>,
  strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
  b: (chunks: ReactNode) => <b>{chunks}</b>,
  i: (chunks: ReactNode) => <i>{chunks}</i>,
  u: (chunks: ReactNode) => <u>{chunks}</u>,
  small: (chunks: ReactNode) => <small>{chunks}</small>,
  mark: (chunks: ReactNode) => <mark>{chunks}</mark>,
} as const;

export type MessageValues = Record<string, string | number | Date>;

export interface TProps {
  /** Full dot-path into the catalog, e.g. `"checkout.shipTo"`. */
  id: string;
  /** ICU placeholder values, e.g. `{count: 3}` for `"{count} items"`. */
  values?: MessageValues;
}

export function T({ id, values }: TProps) {
  const t = useTranslations();
  const locale = useIntlLocale();

  // No catalog for this locale yet: hand the English source to WorldLida and
  // let it translate in the browser, as the whole site used to.
  if (!hasCatalog(locale)) return <L text={String(t.raw(id))} />;

  return <>{t.rich(id, { ...RICH_TAGS, ...values })}</>;
}

/**
 * Imperative form, for strings that must be plain text rather than nodes:
 * `placeholder`, `aria-label`, `alt`, `title`.
 *
 *   const t = useT();
 *   <input placeholder={t("checkout.email")} />
 *
 * Note these get no WorldLida fallback — a string can't be translated
 * asynchronously without a re-render — so on a catalog-less locale they stay
 * English. They were English before this migration too.
 */
export { useTranslations as useT } from "next-intl";
